/**
 * 반복 인스턴스의 범위 확인 — 칸 편집·보드 드래그·선택 바 일괄 (백로그 BA1, 반복 스펙 §6.2).
 *
 * 패널 저장·그리드 드래그는 이미 「이 일정만 / 이후 모두 / 전체」를 물었다(calendar.js·
 * calendar-grid.js). 여기는 나머지 편집 경로가 같은 질문을 하도록 하는 공용 조각이다:
 *
 *   splitScope(els, kind)  → Promise<null | { scope, plain, scoped }>
 *     - els 에 반복 인스턴스(data-series)가 없으면 묻지 않고 { scope:'this', plain: els }.
 *     - 있으면 한 번만 묻는다(kind 'delete' 는 지우기 문구·위험 표시). 취소 = null(아무것도 하지 않는다).
 *     - 'this' 면 전부 plain — 호출자의 평소 길(실행 취소 스택)로 간다.
 *     - 'following'·'all' 이면 보통 일정은 plain(그 답과 무관하게 평소대로), 반복 인스턴스는
 *       시리즈마다 대표 하나(가장 이른 자리표 — "이후 모두" 는 거기서부터)만 scoped 로 모은다:
 *       같은 시리즈의 여러 행에 따로 보내면 서버가 같은 변경을 두 번(날짜는 일수 차를 두 번) 얹는다.
 *   sendScoped(reps, scope, fieldsOf)  → 대표마다 patch + series_scope, 실행 취소 밖, 토스트 + 새로고침
 *   deleteScoped(reps, scope)          → 대표마다 delete + series_scope (소유자만 — 서버가 403)
 *
 * 완료·숨김·정렬·계층(들여쓰기)은 묻지 않는다 — 언제나 그 행만이다(스펙 §5.1).
 * 문구는 패널의 반복 행(#calRepeatRow)의 data-* 에서 온다 — 캘린더 화면마다 패널이 있다.
 */
import { api, fmtCount, showToast, refreshGrid, labels } from './calendar-history.js';
import { appChoose } from './confirm-dialog.js';

function L() {
    const row = document.getElementById('calRepeatRow');
    return row ? row.dataset : {};
}

export function isSeriesEl(el) {
    return !!(el && el.dataset && el.dataset.series);
}

export function askScope(kind) {
    const R = L();
    const del = kind === 'delete';
    return appChoose({
        title: del ? (R.scopeDeleteTitle || '반복 일정을 어떻게 지울까요?') : (R.scopeTitle || '반복 일정을 어떻게 바꿀까요?'),
        choices: [
            { value: 'this', label: R.scopeThis || '이 일정만', primary: true },
            { value: 'following', label: R.scopeFollowing || '이후 모두', danger: del },
            { value: 'all', label: R.scopeAll || '전체', danger: del },
        ],
    });
}

const slotOf = (el) => el.dataset.occurrence || el.dataset.start || '';

export function splitScope(els, kind) {
    const list = (els || []).filter(Boolean);
    if (!list.some(isSeriesEl)) return Promise.resolve({ scope: 'this', plain: list, scoped: [] });
    return askScope(kind).then((scope) => {
        if (scope === null || scope === undefined) return null;
        if (scope === 'this') return { scope, plain: list, scoped: [] };
        const bySeries = new Map();
        list.filter(isSeriesEl).forEach((el) => {
            const cur = bySeries.get(el.dataset.series);
            if (!cur || slotOf(el) < slotOf(cur)) bySeries.set(el.dataset.series, el);
        });
        return { scope, plain: list.filter((el) => !isSeriesEl(el)), scoped: Array.from(bySeries.values()) };
    });
}

function runScoped(reps, send, msg) {
    let n = 0;
    return reps.reduce((p, el) => p.then(() => send(el).then((r) => { n += ((r && r.affected) || 0) + 1; })), Promise.resolve())
        .then(() => { showToast(fmtCount(msg, n)); return refreshGrid(); })
        .catch(() => { showToast(labels().failed, { error: true }); return refreshGrid(); });
}

export function sendScoped(reps, scope, fieldsOf) {
    if (!reps || !reps.length) return Promise.resolve();
    return runScoped(reps, (el) => api.patch(el.dataset.id, Object.assign({}, fieldsOf(el), { series_scope: scope })),
        L().msgChanged || '일정 %d개를 바꿨습니다');
}

export function deleteScoped(reps, scope) {
    if (!reps || !reps.length) return Promise.resolve();
    return runScoped(reps, (el) => api.remove(el.dataset.id, { series_scope: scope }),
        L().msgDeleted || '일정 %d개를 지웠습니다');
}
